<template>
  <div class="export-container">
    <!-- 面包屑 -->
    <el-breadcrumb separator="/">
      <el-breadcrumb-item :to="{ path: 'welcome' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>订单与报名</el-breadcrumb-item>
      <el-breadcrumb-item>统计与导出</el-breadcrumb-item>
    </el-breadcrumb>

    <!-- 搜索栏 -->
    <el-card class="filter-card">
      <div class="search-wrapper">
        <div class="search-toggle" @click="toggleSearch" v-show="isMobile">
          <i :class="isSearchExpanded ? 'el-icon-arrow-down' : 'el-icon-arrow-right'"></i>
          <span>搜索筛选</span>
        </div>
        <div class="search-content" v-show="!isMobile || isSearchExpanded" :class="{ 'search-content-mobile': isMobile }">
          <el-row :gutter="24" align="middle">
            <el-col :span="6">
              <el-input
                placeholder="搜索活动名称"
                v-model="searchQuery.title"
                clearable
                size="medium"
                @clear="fetchActivities()"
                @keyup.enter.native="fetchActivities()">
                <el-button slot="append" icon="el-icon-search" @click="fetchActivities()"></el-button>
              </el-input>
            </el-col>
            <el-col :span="3">
              <el-select v-model="searchQuery.type" size="medium" placeholder="活动类型" @change="fetchActivities()">
                <el-option
                  v-for="(name, id) in activityTypes"
                  :key="id"
                  :label="name"
                  :value="Number(id)">
                </el-option>
              </el-select>
            </el-col>
            <el-col :span="3">
              <el-select v-model="searchQuery.status" size="medium" placeholder="活动状态" @change="fetchActivities()">
                <el-option
                  v-for="(item, id) in statusMap"
                  :key="id"
                  :label="item.text"
                  :value="Number(id)">
                </el-option>
              </el-select>
            </el-col>
            <el-col :span="2">
              <el-button type="default" size="medium" @click="resetSearch()">重置</el-button>
            </el-col>
            <el-col :span="3" class="switch-col">
              <el-switch
                v-model="orderStatusPaid"
                active-text="已付"
                inactive-text="全部"
              />
            </el-col>
            <el-col :span="7">
              <el-button type="primary" size="medium" :disabled="selectedActivities.length === 0" @click="batchExport('orders')">批量导出订单</el-button>
              <el-button type="primary" size="medium" :disabled="selectedActivities.length === 0" @click="batchExport('participants')">批量导出报名人</el-button>
            </el-col>
          </el-row>
        </div>
      </div>
    </el-card>

    <!-- 活动卡片网格 -->
    <div class="activities-grid">
      <el-card
        v-for="activity in activityList"
        :key="activity.activity_id"
        class="activity-card"
        :class="[{ 'activity-card-ended': activity.status === 3 }, { 'activity-card-selected': selectedActivities.includes(activity.activity_id) }]">
        <div class="activity-header">
          <span class="activity-title">{{ activity.title_cn }}</span>
          <el-tag :type="getStatusType(activity.status)" size="small" class="activity-status-tag">
            {{ getStatusText(activity.status) }}
          </el-tag>
        </div>

        <!-- 统计信息展示区 -->
        <div class="activity-stats-card">
          <div class="activity-stats">
            <div class="stat-item">
              <span class="stat-value amount">￥{{ ((orderStatusPaid ? activity.stats?.paid_order_amount : activity.stats?.all_order_amount) || 0).toLocaleString() }}</span>
              <span class="stat-label">{{ orderStatusPaid ? '成交金额' : '订单金额' }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-value">{{ orderStatusPaid ? (activity.stats?.paid_order_count || 0) : (activity.stats?.all_order_count || 0) }}</span>
              <span class="stat-label">{{ orderStatusPaid ? '已付订单' : '全部订单' }}</span>
            </div>
          </div>
          <div class="participant-stats">
            <div class="stat-item">
              <span class="stat-value">{{ orderStatusPaid ? (activity.stats?.paid_order_participants || 0) : (activity.stats?.all_order_participants || 0) }}</span>
              <span class="stat-label">报名人数</span>
            </div>
            <div class="stat-item">
              <span class="stat-value info">{{ orderStatusPaid ? (activity.stats?.current_paid_participants || 0) : (activity.stats?.all_filled_participants_count || 0) }}</span>
              <span class="stat-label">已填信息人数</span>
            </div>
            <div class="stat-item">
              <span class="stat-value danger">{{ orderStatusPaid ? (activity.stats?.lack_paid_participants || 0) : (activity.stats?.all_unfilled_participants_count || 0) }}</span>
              <span class="stat-label">未填信息人数</span>
            </div>
          </div>
        </div>

        <div class="activity-actions">
          <el-checkbox
            v-model="selectedActivities"
            :label="activity.activity_id"
          >
            <span style="display:none"></span>
          </el-checkbox>
          <div>
            <el-button type="primary" size="small" @click="exportOrders(activity.activity_id)">
              <i class="el-icon-download" v-if="!activity.exporting"></i>
              <i class="el-icon-loading" v-else></i>
              导出订单
            </el-button>
            <el-button type="primary" size="small" class="ml8" @click="exportExcel(activity.activity_id)">
              <i class="el-icon-download" v-if="!activity.exporting"></i>
              <i class="el-icon-loading" v-else></i>
              导出报名人
            </el-button>
          </div>
        </div>
      </el-card>
    </div>

    <!-- 分页 -->
    <div class="pagination-wrapper" v-if="total > 0">
      <el-pagination
        @size-change="onSizeChange"
        @current-change="onPageChange"
        :current-page="pageNum"
        :page-sizes="[10, 20, 30, 40]"
        :page-size="pageSize"
        :layout="isMobile ? 'total, prev, pager, next' : 'total, sizes, prev, pager, next, jumper'"
        :total="total">
      </el-pagination>
    </div>
  </div>
</template>

<script>
import filterMixin from '@/utils/filter.js'

export default {
  name: 'Export',
  mixins: [filterMixin],
  data() {
    return {
      sessionKey: 'GeneralExportSearch',
      filterFetchMethod: 'fetchActivities',
      activityList: [],
      total: 0,
      pageNum: 1,
      pageSize: 8,
      searchQuery: {
        title: '',
        type: null,
        status: null
      },
      activityTypes: {
        1: '内地研学',
        2: '疆内研学',
        3: '主题研学',
        4: '订制团'
      },
      statusMap: {
        1: { text: '未开始', type: 'success' },
        2: { text: '进行中', type: 'warning' },
        3: { text: '已结束', type: 'info' }
      },
      selectedActivities: [],
      orderStatusPaid: true,
      isSearchExpanded: false,
      isMobile: false
    }
  },
  watch: {
    searchQuery: {
      handler: 'saveFilters',
      deep: true
    },
    orderStatusPaid: 'saveFilters',
    pageNum: 'saveFilters',
    pageSize: 'saveFilters'
  },
  created() {
    this.checkIsMobile();
    this.fetchActivities()
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    fetchActivities() {
      this.$http({
        method: 'get',
        url: 'activities/stats/orders',
        params: {
          ...this.searchQuery,
          page: this.pageNum,
          size: this.pageSize
        }
      }).then(res => {
        if (!res.success) return this.$message.error(res.message)
        this.activityList = res.data.map(activity => ({
          ...activity,
          exporting: false
        }))
        this.total = res.total
      }).catch(err => {
        this.$message.error(err.response?.data?.message || '获取活动列表失败')
      })
    },

    resetSearch() {
      this.searchQuery = { title: '', type: null, status: null }
      this.pageNum = 1
      this.pageSize = 8
      this.orderStatusPaid = true
      sessionStorage.removeItem(this.sessionKey)
      this.fetchActivities()
    },

    getStatusText(status) {
      return this.statusMap[status]?.text || '未知状态'
    },

    getStatusType(status) {
      return this.statusMap[status]?.type || ''
    },

    // 工具函数：生成唯一短时间戳+随机码
    shortUniqueCode() {
      const ts = Date.now().toString().slice(-5)
      const rand = Math.random().toString(36).slice(-2).toUpperCase()
      return ts + rand
    },

    // 工具函数：清理文件名中的特殊字符
    sanitizeFileName(name) {
      return name.replace(/[\\/:*?"<>|\n\r]/g, '').trim()
    },

    // 导出报名人名单
    exportExcel(activityId) {
      const activity = this.activityList.find(a => a.activity_id === activityId)
      if (!activity) return

      activity.exporting = true
      const params = {}
      if (this.orderStatusPaid) params.order_status = [11, 21, 31, 41, 51, 100] // 已支付状态
      this.$http.get(`/participants/export/enrolls/${activityId}`, {
        params,
        responseType: 'blob'
      }).then(res => {
        const blob = new Blob([res], { type: 'application/vnd.ms-excel' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        link.href = url
        const paidText = this.orderStatusPaid ? '(已付)' : '(全部)'
        const code = this.shortUniqueCode()
        const cleanTitle = this.sanitizeFileName(activity.title_cn)
        link.download = `【${cleanTitle}】_报名人数据${paidText}_${code}.xlsx`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }).catch(err => {
        this.$message.error(err.response?.data?.message || '导出失败')
      }).finally(() => {
        activity.exporting = false
      })
    },

    exportOrders(activityId) {
      const activity = this.activityList.find(a => a.activity_id === activityId)
      if (!activity) return

      activity.exporting = true
      const params = {}
      if (this.orderStatusPaid) params.order_status = [11, 21, 31, 41, 51, 100] // 已支付状态
      this.$http.get(`/orders/export/orders/${activityId}`, {
        params,
        responseType: 'blob'
      }).then(res => {
        const blob = new Blob([res], { type: 'application/vnd.ms-excel' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        link.href = url
        const paidText = this.orderStatusPaid ? '(已付)' : '(全部)'
        const code = this.shortUniqueCode()
        const cleanTitle = this.sanitizeFileName(activity.title_cn)
        link.download = `【${cleanTitle}】_订单数据${paidText}_${code}.xlsx`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }).catch(err => {
        this.$message.error(err.response?.data?.message || '导出失败')
      }).finally(() => {
        activity.exporting = false
      })
    },

    onSizeChange(newSize) {
      this.pageSize = newSize
      this.fetchActivities()
    },

    onPageChange(newPage) {
      this.pageNum = newPage
      this.fetchActivities()
    },

    toggleSearch() {
      this.isSearchExpanded = !this.isSearchExpanded
    },

    checkIsMobile() {
      this.isMobile = window.innerWidth <= 768;
    },

    handleResize() {
      this.checkIsMobile();
    },

    // 批量导出
    batchExport(type) {
      if (!this.selectedActivities.length) {
        this.$message.warning('请先选择活动')
        return
      }
      let url = ''
      let filename = ''
      if (type === 'participants') {
        url = '/participants/export/multi'
        filename = '批量导出_报名人数据'
      } else if (type === 'orders') {
        url = '/orders/export/multi'
        filename = '批量导出_订单数据'
      } else {
        this.$message.error('未知导出类型')
        return
      }
      const ids = this.selectedActivities.join(',')
      const params = { activity_ids: ids }
      if (this.orderStatusPaid) params.order_status = [11, 21, 31, 41, 51, 100] // 已支付状态
      this.$http.get(url, {
        params,
        responseType: 'blob'
      }).then(res => {
        const blob = new Blob([res], { type: 'application/vnd.ms-excel' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        link.href = url
        const paidText = this.orderStatusPaid ? '(已付)' : '(全部)'
        const code = this.shortUniqueCode()
        link.download = `${filename}${paidText}_${code}.xlsx`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }).catch(err => {
        this.$message.error(err.response?.data?.message || '批量导出失败')
      })
    }
  }
}
</script>

<style scoped>
.export-container {
  padding: 0px;
  overflow-x: hidden;
  box-sizing: border-box;
}

.filter-card {
  padding: 0px;
}

.activities-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 20px;
  margin: 20px 0;
}

.activity-card {
  position: relative;
  overflow: hidden;
  border-radius: 14px;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.06);
  padding: 14px 12px 10px 12px;
  background: #fff;
  transition: box-shadow 0.2s;
}

.activity-card:hover {
  box-shadow: 0 4px 24px 0 rgba(64,158,255,0.13);
}

.activity-card-ended {
  opacity: 0.8;
}

.activity-card-selected {
  background: #eff4f9;
}

.activity-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 10px;
}

.activity-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  flex: 1;
  margin-right: 10px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.activity-status-tag {
  margin-left: auto;
  font-size: 12px;
}

.activity-stats-card {
  background: #f8fafc;
  border-radius: 12px;
  padding: 16px 10px 10px 10px;
  margin-bottom: 14px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

.activity-stats, .participant-stats {
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  gap: 10px;
}

.activity-stats {
  margin-bottom: 8px;
}

.participant-stats {
  margin-top: 4px;
}

.activity-stats + .participant-stats {
  border-top: 1px dashed #e4e7ed;
  padding-top: 8px;
  margin-top: 0;
}

.stat-item {
  background: #fff;
  border-radius: 8px;
  padding: 10px 0 6px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.stat-value {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 3px;
  letter-spacing: 0.2px;
}

.stat-value.amount {
  color: #e6a23c;
}

.stat-value.info {
  color: #67c23a;
}

.stat-value.danger {
  color: #f56c6c;
}

.stat-label {
  font-size: 12px;
  color: #7a8ca5;
  letter-spacing: 0.1px;
}

.activity-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 4px;
}

.pagination-wrapper {
  margin-top: 30px;
  text-align: center;
  border-radius: 4px;
  padding: 1px 10px 15px;
  background-color: #fff;
}

.export-container :deep(.el-progress-bar__inner) {
  transition: all 0.3s ease;
}

.export-container :deep(.el-progress-bar__outer) {
  background-color: #f0f2f5;
}

.export-container :deep(.el-progress__text) {
  font-size: 12px !important;
}

.batch-export-btn-simple {
  /* 移除自定义样式 */
  all: unset;
}
.switch-col {
  display: flex;
  align-items: center;
  height: 35px;
}

/* 只针对卡片底部的checkbox */
.activity-actions .el-checkbox__label {
  display: none !important;
}

.search-wrapper {
  margin-bottom: 10px;
}

.search-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  color: #666;
  font-size: 14px;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  transition: color 0.3s;
}

.search-toggle:hover {
  color: #409eff;
}

.search-toggle i {
  font-size: 14px;
}

.search-content {
  padding-top: 10px;
}

@media (max-width: 768px) {
  .export-container {
    padding: 0 5px;
  }

  .filter-card {
    padding: 0 10px;
  }

  .search-content .el-row {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .search-content .el-row .el-col {
    width: 100% !important;
    margin-bottom: 5px;
  }

  .search-toggle {
    padding: 10px 0;
    font-size: 13px;
  }

  .activities-grid {
    grid-template-columns: 1fr;
    gap: 10px;
    margin: 10px 0;
  }

  .activity-card {
    padding: 10px;
    border-radius: 10px;
  }

  .activity-title {
    font-size: 13px;
  }

  .activity-stats-card {
    padding: 12px;
    border-radius: 8px;
  }

  .activity-stats,
  .participant-stats {
    gap: 8px;
  }

  .stat-item {
    padding: 10px 0;
    border-radius: 8px;
  }

  .stat-value {
    font-size: 20px;
    font-weight: 700;
  }

  .stat-label {
    font-size: 12px;
  }

  .activity-actions {
    flex-direction: row;
  }

  .pagination-wrapper {
    margin-top: 10px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    touch-action: pan-x;
  }

  .pagination-wrapper .el-pagination {
    font-size: 10px;
    justify-content: flex-start;
    min-width: max-content;
  }

  .pagination-wrapper .btn-prev,
  .pagination-wrapper .btn-next {
    width: 26px;
    height: 26px;
    line-height: 26px;
    font-size: 11px;
    padding: 0;
  }

  .pagination-wrapper .el-pager li {
    padding: 0 2px;
    min-width: 22px;
    height: 26px;
    line-height: 26px;
    font-size: 10px;
  }

  .pagination-wrapper .el-pagination__total {
    font-size: 10px;
    margin-right: 8px;
  }
}
</style>
